import { HttpStatusCode } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription, combineLatest, filter, of, takeUntil, takeWhile, withLatestFrom } from 'rxjs';
import { AccountInfoActions } from '../../account-info-header/actions';
import { selectAgreementText, selectCreateWireTemplateCallCompleted, selectCreateWireTemplateCallSuccessful, selectShouldDisplayAgreement, selectSubAccountId, selectSubmitDeleteStandingAuthState, selectUserContext } from '../../account-info-header/reducers';
import * as fromEauth from '../../setup-eAuthorization/actions/eAuthorization.actions';
import { SharedActions } from '../../shared/actions';
import { initializePage } from '../../shared/actions/shared.actions';
import { selectStandingAuthTableEditTransactionClicked, selectViewInContext } from '../../shared/reducers';
import { TableComponentState } from '../../shared/types/TableComponentState';
import { Constants, navRouteConstants, transactionTypes, pageInContext } from '../../shared/utilities/Constants';
import { AchActions, AgreementActions, StandingAuthorizationsTableActions, WireActions } from '../actions';
import { resetSubmitCancelTransactionState } from '../actions/standing-authorizations-table.actions';
import { TaxWithholdingFields, TransactionForm, TransactionFormServiceError, WireForm } from '../models/form/TransactionForm';
import { CashieringRequestApr } from '../models/internal-transfer/CashieringRequestApr';
import { RecurringRequest } from '../models/internal-transfer/RecurringRequest';
import { SloaRequest } from '../models/internal-transfer/SloaRequest';
import { selectIsBundled, selectIsUseStandingAuth, selectStagedTransactionForm, selectStagedTransactionType, selectTaxWithholdings, selectTemplateNicknameFromModal, selectTransactionFormServiceError, selectTweRecurringTransactions, selectTweStandingAuth, selectTweTableState, selectTweUpcomingTransactions } from '../reducers';
import { selectTemplateId } from '../../create-request/reducers';
import { selectTemplatesList } from '../../account-info-header/reducers';
import { selectAuthorizeCallFailed, selectAuthorizeCallSuccessful, selectHasViewMoveMoney } from '../../authorize/reducers';
import { DraftCommonWire } from '../models/wire/WireTemplatesResponse';
import { CoreService, ICurrentContext } from '@charlesschwab/sac-ui-core';
import { ContextType } from '@charlesschwab/as-ui-core';
import { ContextService } from 'projects/integration/src/app/context.service';
import { ValidateWireFlagsService } from '../../shared/services/validate-WireFlags.service';

@Component({
  standalone: false,
  selector: 'mm-create-request',
  templateUrl: './create-request.component.html',
  styleUrls: ['./create-request.component.scss'],
})
export class CreateRequestComponent implements OnInit, OnDestroy {
  destroy$: Subject<void> = new Subject();
  accountIdSub: Subscription;
  isAgreementFinishedSub: Subscription;
  createRequestStep$: Observable<string>;
  shouldDisplayAgreement$: Observable<boolean>;
  showTweModal$: Subject<boolean> = new Subject();
  showOverdraftRulesPopup$: Subject<string> = new Subject();
  accountInContext: boolean = false;
  masterAccountInContext: boolean = false;
  palAccountInContext: boolean = false;
  showAgreementAcceptButton: boolean = false;
  firstTimeUserAgreementText: string = '';
  subAccountId: string = '';
  tweUpcomingTransactions$: Observable<(CashieringRequestApr | RecurringRequest)[]>;
  tweStandingAuth$: Observable<SloaRequest[]>;
  tweRecurringTransactions$: Observable<RecurringRequest[]>;
  taxWithholdings$: Observable<TaxWithholdingFields>;
  tweTableState$: Observable<TableComponentState>;
  saveAsTemplateClicked$: Subject<boolean> = new Subject();
  hasServiceError: boolean;
  hasBadRequestServiceError: boolean;
  hasAtpTimeoutError: boolean;
  transactionFormServiceError$: Observable<TransactionFormServiceError>;
  serviceErrorList: any[];
  showSaveAsTemplate: boolean;
  viewState: string;
  isUpdateTemplate: boolean;
  hasViewMoneyAccess$: Observable<boolean>;
  authorizedFinished$: Observable<boolean>;
  authorizedFailed$: Observable<boolean>;
  userInContext: string;
  currentContext: ICurrentContext = {
    contextId: 0,
    contextType: ContextType.Unknown,
    firmId: 0,
    firmName: '',
    title: '',
  };

  constructor(
    private store: Store,
    private router: Router,
    private toastr: ToastrService,
    private coreService: CoreService,
    private cdr: ChangeDetectorRef,
    private contextService: ContextService,
    private validateWireFlagsService: ValidateWireFlagsService
  ) { }

  ngOnInit(): void {

    this.authorizedFinished$ = this.store.select(selectAuthorizeCallSuccessful);
    this.authorizedFailed$ = this.store.select(selectAuthorizeCallFailed);
    this.authorizedFinished$.pipe(takeUntil(this.destroy$)).subscribe(finished => {
      if (finished){
        this.hasViewMoneyAccess$ = this.store.pipe(select(selectHasViewMoveMoney));
      } else {
        this.hasViewMoneyAccess$ = of(false);
      }
    });

    this.transactionFormServiceError$ = this.store.select(selectTransactionFormServiceError);
    this.transactionFormServiceError$.pipe(takeUntil(this.destroy$)).subscribe(val => {
      if (val?.url.includes('JournalListAcknowledge')) {
        if (val?.status === HttpStatusCode.BadRequest && val.errorList) {
          this.hasBadRequestServiceError = true;
          this.serviceErrorList = val.errorList.map((err) => !!err.detail ? err.detail : err.title);
        } else if (val?.errorList?.map(err => err.detail).includes(Constants.atpRequestTimeoutErrorDetail) && val.errorList) {
          this.hasServiceError = val.hasError;
          this.hasBadRequestServiceError = false;
          this.hasAtpTimeoutError = true;
          this.serviceErrorList = [Constants.atpRequestTimeoutErrorMessage];
        } else {
          this.hasServiceError = val.hasError;
          this.hasBadRequestServiceError = false;
          this.hasAtpTimeoutError = false;
        }
      }
    });

    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe((view) => {
      this.viewState = view;
    });

    this.createRequestStep$ = this.store.select(selectViewInContext);


    this.store.dispatch(initializePage());
    this.accountIdSub = this.store.pipe(select(selectSubAccountId), withLatestFrom(this.createRequestStep$), filter((id, _createStep) => !!id)).subscribe(([id, createStep]) => {
      if (id?.length > 0 && createStep !== pageInContext.viewMsv) {
        this.subAccountId = id;
        this.store.dispatch(AccountInfoActions.loadTransactions({ accountId: id }));
        this.store.dispatch(fromEauth.loadEAuthClients({ accountId: id }));
        // data from this call is required for submit and create option
        this.store.dispatch(fromEauth.loadCreateStandingAuthEAuthClients({ accountId: id }));
      }
    });
    this.shouldDisplayAgreement$ = this.store.select(selectShouldDisplayAgreement);
    this.store.select(selectShouldDisplayAgreement)
      .pipe(takeUntil(this.destroy$))
      .subscribe(display => {
        if (display) this.showAgreementAcceptButton = false;
      });
    this.store.select(selectAgreementText)
      .pipe(takeUntil(this.destroy$))
      .subscribe(text => {
        this.firstTimeUserAgreementText = text;
      });

    this.tweUpcomingTransactions$ = this.store.select(selectTweUpcomingTransactions);
    this.tweStandingAuth$ = this.store.select(selectTweStandingAuth);
    this.tweRecurringTransactions$ = this.store.select(selectTweRecurringTransactions);
    this.taxWithholdings$ = this.store.select(selectTaxWithholdings);
    this.tweTableState$ = this.store.select(selectTweTableState);

    this.store.dispatch(SharedActions.createRequestComponentLoaded());

    combineLatest([
      this.store.select(selectCreateWireTemplateCallSuccessful),
      this.store.select(selectStagedTransactionType),
      this.store.select(selectIsUseStandingAuth),
      this.store.select(selectIsBundled)
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(
          ([createTemplateSucceeded, transactionType, isUseStandingAuth, isBundled]) =>
            transactionType === transactionTypes.wire
            && !createTemplateSucceeded && !isUseStandingAuth && !isBundled
        )
      )
      .subscribe(() => {
        this.showSaveAsTemplate = true;
      });

    this.store
      .select(selectCreateWireTemplateCallCompleted)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectCreateWireTemplateCallSuccessful),
        ),
        filter(([completed, _successful]) => completed)
      )
      .subscribe(([_completed, successful]) => {
        if (successful){
          this.toastr.success("Saved as a template");
        } else {
          this.toastr.error("Failed to save as a template");
        }
      });

    this.store
      .select(selectStandingAuthTableEditTransactionClicked)
      .pipe(
        takeUntil(this.destroy$),
        filter((editTransactionClicked) => editTransactionClicked)
      )
      .subscribe((_editTransactionClicked) => {
        const currentUrl: string = window.location.href;
        if (this.contextService.isMonolithUrl(currentUrl)) {
          this.router.navigate([navRouteConstants.standingAuth])
        } else {
          let encodedContext = decodeURIComponent(this.coreService.currentContext.encodedContext as string);
          this.router.navigate([navRouteConstants.standingAuth], { queryParams: { ec: encodedContext } });
        }
      });

    this.coreService.currentContextUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        if (context) {
          this.currentContext = context;
        }
        if (this.currentContext?.contextType === ContextType.Firm) {
          this.masterAccountInContext = false;
          this.accountInContext = false;
          this.palAccountInContext = false;
        } else if (this.currentContext?.contextType === ContextType.Account) {
          this.accountInContext = true;
          this.masterAccountInContext = false;
          this.palAccountInContext = false;
        } else if (this.currentContext?.contextType === ContextType.Master) {
          this.masterAccountInContext = true;
          this.accountInContext = false;
          this.palAccountInContext = false;
        } else if (this.currentContext?.contextType === ContextType.PALAccount && this.validateWireFlagsService.enablePalWireCreateTransaction()) {
          this.palAccountInContext = true;
          this.masterAccountInContext = false;
          this.accountInContext = false;
        } else if (this.currentContext?.contextType === ContextType.PALAccount && !this.validateWireFlagsService.enablePalWireCreateTransaction()) {
          this.palAccountInContext = false;
          this.masterAccountInContext = false;
          this.accountInContext = false;
        } else {
          this.masterAccountInContext = false;
          this.accountInContext = false;
          this.palAccountInContext = false;
        }
        this.store.dispatch(SharedActions.createRequestComponentLoaded());
      });

    if (this.coreService?.currentContext?.contextType === ContextType.Firm) {
      this.masterAccountInContext = false;
      this.accountInContext = false;
      this.palAccountInContext = false;
    } else if (this.coreService?.currentContext?.contextType === ContextType.Account) {
      this.accountInContext = true;
      this.masterAccountInContext = false;
      this.palAccountInContext = false;
    } else if (this.coreService?.currentContext?.contextType === ContextType.Master) {
      this.masterAccountInContext = true;
      this.accountInContext = false;
      this.palAccountInContext = false;
    } else if (this.coreService?.currentContext?.contextType === ContextType.PALAccount && this.validateWireFlagsService.enablePalWireCreateTransaction()) {
      this.palAccountInContext = true;
      this.masterAccountInContext = false;
      this.accountInContext = false;
    } else if (this.coreService?.currentContext?.contextType === ContextType.PALAccount && !this.validateWireFlagsService.enablePalWireCreateTransaction()) {
      this.palAccountInContext = false;
      this.masterAccountInContext = false;
      this.accountInContext = false;
    } else {
      this.masterAccountInContext = false;
      this.accountInContext = false;
      this.palAccountInContext = false;
    }

    combineLatest([
      this.store.select(selectUserContext)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userContext]) => {
        this.userInContext = userContext;
        this.cdr.detectChanges();
      });
  }

  showCreateTransactionPage() {
    this.store.dispatch(AccountInfoActions.loadTransactions({ accountId: this.subAccountId }));
    this.store.dispatch(fromEauth.loadEAuthClients({ accountId: this.subAccountId }));
    // data from this call is required for submit and create option
    this.store.dispatch(fromEauth.loadCreateStandingAuthEAuthClients({ accountId: this.subAccountId }));
  }

  ngOnDestroy(): void {
    if (this.accountIdSub) this.accountIdSub.unsubscribe();
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  deleteNewAchProfileFromReview(processCancelCall: boolean): void { //used for ACH flow
    if (processCancelCall) {
      this.store.select(selectSubmitDeleteStandingAuthState)
        .pipe(takeWhile(state => state.isCancelTransactionCallFinished === false, true))
        .subscribe(deleteSloaState => {
          if (deleteSloaState.isCancelTransactionCallFinished) {
            if (deleteSloaState.hasCancelTransactionError) {
              this.store.dispatch(StandingAuthorizationsTableActions.refresh({ accountId: this.subAccountId }));
              this.toastr.success("On-Request profile created, please review in the Standing Authorization table");
            } else {
              this.store.dispatch(AchActions.deleteProfileFromReviewPageSuccessful());
            }
            this.store.dispatch(resetSubmitCancelTransactionState());
          }
        });
    }
  }

  saveAsTemplateClicked($event: boolean): void {
    this.saveAsTemplateClicked$.next($event);
  }

  saveTemplate($event: boolean): void {
    if (!$event){
      return;
    }
    let transactionType: string = '';
    let transactionForm: TransactionForm = {
      transactionType: '',
      transactionDetails: {},
      showCashieringIRAAgreement: false,
      serviceError: {
        status: 0,
        url: '',
        name: '',
        message: '',
        correlationId: '',
        hasError: false,
      },
      prefilledFlow: {
        state: '',
        name: ''
      },
      createTransactionInProgress: false,
      loading: false,
      error: '',
      hasError: false,
      acknowledgeCallV3FailedForToggleOn: false,
      acknowledgeCallV3Failed: false
    };
    let templateList: DraftCommonWire[] = [];
    let templateId: string | undefined = '';

    this.store.select(selectTemplateNicknameFromModal)
      .pipe(
        takeUntil(this.destroy$))
      .subscribe((nickName) => {
        if (this.viewState === pageInContext.confirmation){
          this.store.select(selectStagedTransactionType).pipe(takeUntil(this.destroy$)).subscribe(selectStagedTransactionType => {
            transactionType = selectStagedTransactionType
          });
          this.store.select(selectStagedTransactionForm).pipe(takeUntil(this.destroy$)).subscribe(selectStagedTransactionForm => {
            transactionForm = selectStagedTransactionForm
          });
          this.store.select(selectTemplatesList).pipe(takeUntil(this.destroy$)).subscribe(selectTemplatesList => {
            templateList = selectTemplatesList
          });
          this.store.select(selectTemplateId).pipe(takeUntil(this.destroy$)).subscribe(selectTemplateId => {
            templateId = selectTemplateId
          });
          const templateInContext = templateList?.find(template => template?.draftId === templateId);

          if (transactionType == 'wire' && templateInContext && !templateInContext?.draftObject?.isIsoCompliant) { //updating a non-ISO compliant wire template
            this.store.dispatch(
              WireActions.callWiresPatchTemplate({
                currentTemplate: templateInContext,
                nickname: nickName,
                form: transactionForm as WireForm
              })
            );
          } else {
            this.store.dispatch(
              WireActions.callWiresCreateTemplate({
                templateNickname: nickName,
              }));
          }

        }else if (this.viewState === pageInContext.input){
          this.store.dispatch(
            WireActions.callWireCreateTemplateFromInput({
              templateNickname: nickName
            })
          );
        }
      })
  }

  showTweModal($event: boolean): void {
    this.showTweModal$.next($event);
  }

  showOverdraftRulesPopup($event: string): void {
    this.showOverdraftRulesPopup$.next($event);
  }

  checkboxDisclaimerClickedShowsAgreementAcceptButton(target: EventTarget | null) {
    const checkBox = target as HTMLInputElement;
    this.showAgreementAcceptButton = checkBox.checked;
  }

  onFirstTimeAgreementSubmitClick(): void {
    this.store.dispatch(AgreementActions.submitFirstTimeUserAgreement());
  }

  hideErrorBannerAfterSuccessful() :void {
    this.hasServiceError = false;
    this.hasBadRequestServiceError = false;
    this.hasAtpTimeoutError = false;
  }

  get showMasterStatusView():boolean {
    return this.userInContext === 'MASTER'
  }
}
